import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle2, FileText, Shield } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TermsAcceptanceModal({ open, onAccepted, userName }) {
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedMarketing, setAcceptedMarketing] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleAccept = async () => {
    if (!acceptedTerms) {
      toast.error('Debes aceptar los términos y condiciones para continuar');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/aceptar-terminos`, {
        acepto_marketing: acceptedMarketing
      }, { withCredentials: true });
      
      toast.success('¡Bienvenido a Kinosis!');
      onAccepted();
    } catch (error) {
      toast.error('Error al guardar preferencias');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-lg bg-white" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-purple-600" />
            ¡Bienvenido a Kinosis{userName ? `, ${userName.split(' ')[0]}` : ''}!
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            Antes de continuar, por favor lee y acepta nuestras políticas.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Términos y Condiciones - Obligatorio */}
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="terms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
              />
              <div className="flex-1">
                <label htmlFor="terms" className="font-medium text-slate-900 cursor-pointer">
                  Acepto los Términos y Condiciones <span className="text-red-500">*</span>
                </label>
                <p className="text-sm text-slate-600 mt-1">
                  He leído y acepto los{' '}
                  <a href="/terminos" target="_blank" className="text-purple-600 hover:underline">
                    Términos y Condiciones
                  </a>{' '}
                  y la{' '}
                  <a href="/privacidad" target="_blank" className="text-purple-600 hover:underline">
                    Política de Privacidad
                  </a>.
                </p>
              </div>
            </div>
          </div>

          {/* Marketing - Opcional */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="marketing"
                checked={acceptedMarketing}
                onChange={(e) => setAcceptedMarketing(e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
              />
              <div className="flex-1">
                <label htmlFor="marketing" className="font-medium text-slate-900 cursor-pointer">
                  Acepto recibir comunicaciones comerciales deportivas
                </label>
                <p className="text-sm text-slate-600 mt-1">
                  Quiero recibir información sobre: seguros deportivos, equipamiento, indumentaria, suplementación, promociones de Kinosis y servicios relacionados con el deporte.
                </p>
                <p className="text-xs text-blue-600 mt-2">
                  (Opcional - puedes cambiar esta preferencia en cualquier momento)
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <Button
            onClick={handleAccept}
            disabled={!acceptedTerms || loading}
            className="w-full bg-purple-600 hover:bg-purple-700 h-12"
          >
            {loading ? (
              'Guardando...'
            ) : (
              <>
                <CheckCircle2 className="w-5 h-5 mr-2" />
                Continuar
              </>
            )}
          </Button>
          
          <p className="text-xs text-center text-slate-500">
            Tu privacidad es importante para nosotros. No compartimos tus datos con terceros.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
